import React, { useState } from 'react';
import { propertyData } from '../mock';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogTrigger } from '../components/ui/dialog';
import { Separator } from '../components/ui/separator';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { toast } from 'sonner';
import { 
  Home as HomeIcon, 
  Bed, 
  Bath, 
  Users, 
  Square, 
  MapPin, 
  Phone, 
  Mail,
  Waves,
  Anchor,
  Wifi,
  Car,
  Wind,
  Coffee,
  Tv,
  UtensilsCrossed,
  Trees,
  Flame,
  CheckCircle2,
  Calendar as CalendarIcon,
  ChevronRight,
  Menu
} from 'lucide-react';

const Home = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedImage, setSelectedImage] = useState(null);
  const [showPhone, setShowPhone] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    checkIn: '',
    checkOut: '',
    guests: '',
    message: ''
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/inquiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success(result.message || 'Inquiry submitted! We will contact you soon.');
        setFormData({ name: '', email: '', phone: '', checkIn: '', checkOut: '', guests: '', message: '' });
      } else {
        toast.error(result.message || 'Failed to submit inquiry. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error('Failed to submit inquiry. Please try again later.');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Waves className="w-8 h-8 text-teal-600" />
              <div>
                <h1 className="text-2xl font-bold text-slate-900">{propertyData.name}</h1>
                <p className="text-sm text-slate-600">{propertyData.tagline}</p>
              </div>
            </div>
            <nav className="hidden md:flex items-center gap-6">
              <a href="#overview" className="text-slate-700 hover:text-teal-600 transition-colors">Overview</a>
              <a href="#gallery" className="text-slate-700 hover:text-teal-600 transition-colors">Gallery</a>
              <a href="#amenities" className="text-slate-700 hover:text-teal-600 transition-colors">Amenities</a>
              <a href="#rates" className="text-slate-700 hover:text-teal-600 transition-colors">Rates</a>
              <a href="/blog" className="text-slate-700 hover:text-teal-600 transition-colors">Blog</a>
              <a href="#contact" className="text-slate-700 hover:text-teal-600 transition-colors">Contact</a>
            </nav>
            
            {/* Mobile Menu */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon">
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px]">
                <nav className="flex flex-col gap-6 mt-8">
                  <a 
                    href="#overview" 
                    className="text-lg text-slate-700 hover:text-teal-600 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Overview
                  </a>
                  <a 
                    href="#gallery" 
                    className="text-lg text-slate-700 hover:text-teal-600 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Gallery
                  </a>
                  <a 
                    href="#amenities" 
                    className="text-lg text-slate-700 hover:text-teal-600 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Amenities
                  </a>
                  <a 
                    href="#rates" 
                    className="text-lg text-slate-700 hover:text-teal-600 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Rates
                  </a>
                  <a 
                    href="/blog" 
                    className="text-lg text-slate-700 hover:text-teal-600 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Blog
                  </a>
                  <a 
                    href="#contact" 
                    className="text-lg text-slate-700 hover:text-teal-600 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Contact
                  </a>
                  <Separator />
                  <a 
                    href="#inquiry"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Button className="w-full bg-teal-600 hover:bg-teal-700 text-white">
                      Book Now
                    </Button>
                  </a>
                </nav>
              </SheetContent>
            </Sheet>
            
            <Button className="bg-teal-600 hover:bg-teal-700 text-white hidden md:inline-flex">
              <a href="#inquiry">Book Now</a>
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative h-[600px] overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={propertyData.heroImage} 
            alt="Casa Bahiamar" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/40 to-transparent"></div>
        </div>
        <div className="relative container mx-auto px-4 h-full flex items-end pb-16">
          <div className="text-white max-w-3xl">
            <h2 className="text-5xl md:text-6xl font-bold mb-4 leading-tight">
              Luxury Waterfront Living in the Florida Keys
            </h2>
            <p className="text-xl mb-6 text-slate-100">
              Experience the ultimate Key Largo retreat with 5 bedrooms, heated pool, and direct ocean access
            </p>
            <div className="inline-block bg-amber-500 text-slate-900 px-6 py-3 rounded-lg font-semibold mb-6">
              28-Day Minimum Stay Required
            </div>
            <div className="flex flex-wrap gap-6 mb-8">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-lg">
                <Bed className="w-5 h-5" />
                <span className="font-semibold">{propertyData.stats.bedrooms} Bedrooms</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-lg">
                <Bath className="w-5 h-5" />
                <span className="font-semibold">{propertyData.stats.bathrooms} Bathrooms</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-lg">
                <Users className="w-5 h-5" />
                <span className="font-semibold">Sleeps {propertyData.stats.sleeps}</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-lg">
                <Square className="w-5 h-5" />
                <span className="font-semibold">{propertyData.stats.sqft} sqft</span>
              </div>
            </div>
            <Button size="lg" className="bg-teal-600 hover:bg-teal-700 text-white text-lg px-8">
              <a href="#inquiry" className="flex items-center gap-2">
                Check Availability <ChevronRight className="w-5 h-5" />
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section id="overview" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-slate-900 mb-4">Welcome to Casa Bahiamar</h2>
              <Separator className="w-24 h-1 bg-teal-600 mx-auto" />
            </div>
            
            <div className="prose prose-lg max-w-none text-slate-700 mb-12 leading-relaxed whitespace-pre-line">
              {propertyData.description}
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <Card className="border-teal-100 hover:border-teal-300 transition-all hover:shadow-lg">
                <CardHeader>
                  <Anchor className="w-10 h-10 text-teal-600 mb-2" />
                  <CardTitle>Boat Friendly</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600">Direct canal access with boat dock and trailer parking</p>
                </CardContent>
              </Card>

              <Card className="border-teal-100 hover:border-teal-300 transition-all hover:shadow-lg">
                <CardHeader>
                  <HomeIcon className="w-10 h-10 text-teal-600 mb-2" />
                  <CardTitle>Luxury Estate</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600">6,500 sq ft of elegantly designed living space</p>
                </CardContent>
              </Card>

              <Card className="border-teal-100 hover:border-teal-300 transition-all hover:shadow-lg">
                <CardHeader>
                  <MapPin className="w-10 h-10 text-teal-600 mb-2" />
                  <CardTitle>Prime Location</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600">26 miles from Miami, near John Pennekamp Park</p>
                </CardContent>
              </Card>
            </div>

            <div className="bg-teal-50 rounded-2xl p-8 border border-teal-100">
              <h3 className="text-2xl font-bold text-slate-900 mb-6">Key Features</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {propertyData.features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Photo Gallery</h2>
            <p className="text-slate-600 text-lg">Explore the beauty of Casa Bahiamar</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-7xl mx-auto">
            {propertyData.gallery.map((image, index) => (
              <Dialog key={index}>
                <DialogTrigger asChild>
                  <div 
                    className="relative aspect-square overflow-hidden rounded-lg cursor-pointer group"
                    onClick={() => setSelectedImage(image)}
                  >
                    <img 
                      src={image.url} 
                      alt={image.caption}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/40 transition-all duration-300 flex items-center justify-center">
                      <span className="text-white font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        View Photo
                      </span>
                    </div>
                  </div>
                </DialogTrigger>
                <DialogContent className="max-w-4xl">
                  <img src={image.url} alt={image.caption} className="w-full rounded-lg" />
                  <p className="text-center text-slate-600 mt-4">{image.caption}</p>
                </DialogContent>
              </Dialog>
            ))}
          </div>
        </div>
      </section>

      {/* Amenities Section */}
      <section id="amenities" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">World-Class Amenities</h2>
            <p className="text-slate-600 text-lg">Everything you need for a perfect vacation</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <Card className="border-slate-200">
              <CardHeader>
                <Trees className="w-8 h-8 text-teal-600 mb-2" />
                <CardTitle>Outdoor</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {propertyData.amenities.outdoors.map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-slate-700">
                      <div className="w-1.5 h-1.5 bg-teal-600 rounded-full"></div>
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader>
                <Tv className="w-8 h-8 text-teal-600 mb-2" />
                <CardTitle>Entertainment</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {propertyData.amenities.entertainment.map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-slate-700">
                      <div className="w-1.5 h-1.5 bg-teal-600 rounded-full"></div>
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader>
                <Wind className="w-8 h-8 text-teal-600 mb-2" />
                <CardTitle>Essential</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {propertyData.amenities.essential.map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-slate-700">
                      <div className="w-1.5 h-1.5 bg-teal-600 rounded-full"></div>
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader>
                <UtensilsCrossed className="w-8 h-8 text-teal-600 mb-2" />
                <CardTitle>Kitchen</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {propertyData.amenities.kitchen.map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-slate-700">
                      <div className="w-1.5 h-1.5 bg-teal-600 rounded-full"></div>
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader>
                <CheckCircle2 className="w-8 h-8 text-teal-600 mb-2" />
                <CardTitle>Special Features</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {propertyData.amenities.special.map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-slate-700">
                      <div className="w-1.5 h-1.5 bg-teal-600 rounded-full"></div>
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <MapPin className="w-12 h-12 text-teal-600 mx-auto mb-4" />
              <h2 className="text-4xl font-bold text-slate-900 mb-4">Location</h2>
              <p className="text-slate-600 text-lg">{propertyData.location}</p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
              <p className="text-slate-700 leading-relaxed whitespace-pre-line">
                {propertyData.locationInfo}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Rates & Calendar Section */}
      <section id="rates" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-slate-900 mb-4">Rates & Availability</h2>
              <Separator className="w-24 h-1 bg-teal-600 mx-auto" />
            </div>

            <div className="grid md:grid-cols-2 gap-8 items-start">
              <Card className="border-teal-200">
                <CardHeader className="bg-teal-50">
                  <CardTitle className="text-2xl">Monthly Rate</CardTitle>
                  <CardDescription>28-day minimum stay required</CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="text-5xl font-bold text-teal-600 mb-2">
                    {propertyData.rates.monthly}
                    <span className="text-xl text-slate-600 font-normal">/month</span>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6">
                    <p className="text-amber-900 font-semibold text-center">
                      Minimum Stay: 28 Days
                    </p>
                  </div>
                  <div className="space-y-3 text-slate-700">
                    <p className="flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-teal-600" />
                      All utilities included
                    </p>
                    <p className="flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-teal-600" />
                      Heated pool maintenance
                    </p>
                    <p className="flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-teal-600" />
                      Full property access
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CalendarIcon className="w-5 h-5 text-teal-600" />
                    Check Availability
                  </CardTitle>
                  <CardDescription>Select your preferred dates</CardDescription>
                </CardHeader>
                <CardContent>
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    className="rounded-md border border-slate-200 w-full"
                  />
                  <p className="text-sm text-slate-600 mt-4">
                    * Availability shown is approximate. Contact us to confirm dates.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Inquiry Form Section */}
      <section id="inquiry" className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-slate-900 mb-4">Request to Book</h2>
              <p className="text-slate-600 text-lg">Send us your inquiry and we'll get back to you shortly</p>
              <p className="text-amber-700 font-semibold mt-2">Please note: 28-day minimum rental required</p>
            </div>

            <Card className="border-slate-200 shadow-xl">
              <CardContent className="pt-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="John Doe"
                        required
                        className="border-slate-300"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="john@example.com"
                        required
                        className="border-slate-300"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="(555) 123-4567"
                      className="border-slate-300"
                    />
                  </div>

                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="checkIn">Check-In Date</Label>
                      <Input
                        id="checkIn"
                        name="checkIn"
                        type="date"
                        value={formData.checkIn}
                        onChange={handleInputChange}
                        className="border-slate-300"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="checkOut">Check-Out Date</Label>
                      <Input
                        id="checkOut"
                        name="checkOut"
                        type="date"
                        value={formData.checkOut}
                        onChange={handleInputChange}
                        className="border-slate-300"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="guests">Number of Guests</Label>
                      <Input
                        id="guests"
                        name="guests"
                        type="number"
                        value={formData.guests}
                        onChange={handleInputChange}
                        placeholder="Max 10"
                        min="1"
                        max="10"
                        className="border-slate-300"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Message</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Tell us about your stay..."
                      rows={5}
                      className="border-slate-300"
                    />
                  </div>

                  <Button type="submit" size="lg" className="w-full bg-teal-600 hover:bg-teal-700 text-white text-lg">
                    Submit Inquiry
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-slate-900 mb-4">Contact Owner</h2>
              <Separator className="w-24 h-1 bg-teal-600 mx-auto" />
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <Card className="border-teal-100 text-center hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <Phone className="w-12 h-12 text-teal-600 mx-auto mb-4" />
                  <h3 className="font-semibold text-slate-900 mb-2">Phone</h3>
                  {showPhone ? (
                    <a href={`tel:${propertyData.contact.phone}`} className="text-teal-600 hover:underline text-lg">
                      {propertyData.contact.phone}
                    </a>
                  ) : (
                    <Button 
                      onClick={() => setShowPhone(true)} 
                      variant="outline" 
                      className="border-teal-600 text-teal-600 hover:bg-teal-50"
                    >
                      Show Phone Number
                    </Button>
                  )}
                </CardContent>
              </Card>

              <Card className="border-teal-100 text-center hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <Mail className="w-12 h-12 text-teal-600 mx-auto mb-4" />
                  <h3 className="font-semibold text-slate-900 mb-2">Email</h3>
                  {showEmail ? (
                    <a href={`mailto:${propertyData.contact.email}`} className="text-teal-600 hover:underline break-all">
                      {propertyData.contact.email}
                    </a>
                  ) : (
                    <Button 
                      onClick={() => setShowEmail(true)} 
                      variant="outline" 
                      className="border-teal-600 text-teal-600 hover:bg-teal-50"
                    >
                      Show Email
                    </Button>
                  )}
                </CardContent>
              </Card>

              <Card className="border-teal-100 text-center hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <MapPin className="w-12 h-12 text-teal-600 mx-auto mb-4" />
                  <h3 className="font-semibold text-slate-900 mb-2">Location</h3>
                  <p className="text-slate-600">{propertyData.location}</p>
                </CardContent>
              </Card>
            </div>

            <Card className="mt-8 bg-slate-50 border-slate-200">
              <CardHeader>
                <CardTitle>About the Owner</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700 leading-relaxed">{propertyData.ownerInfo}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Policies Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-slate-900 mb-4">Policies</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle>Rental Requirements</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-4">
                    <p className="font-bold text-amber-900 mb-1">Minimum Stay Requirement</p>
                    <p className="text-amber-800">This property requires a minimum rental period of 28 days (4 weeks).</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle>General Policies</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-700 leading-relaxed">{propertyData.policies.general}</p>
                </CardContent>
              </Card>

              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle>Cancellation Policy</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-700 leading-relaxed">{propertyData.policies.cancellation}</p>
                </CardContent>
              </Card>

              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle>Accessibility</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-700 leading-relaxed">{propertyData.policies.accessibility}</p>
                </CardContent>
              </Card>

              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle>Additional Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-slate-700">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-teal-600" />
                      {propertyData.policies.events}
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-teal-600" />
                      {propertyData.policies.children}
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Waves className="w-8 h-8 text-teal-400" />
                <h3 className="text-xl font-bold">{propertyData.name}</h3>
              </div>
              <p className="text-slate-400">{propertyData.tagline}</p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-slate-400">
                <li><a href="#overview" className="hover:text-teal-400 transition-colors">Overview</a></li>
                <li><a href="#gallery" className="hover:text-teal-400 transition-colors">Gallery</a></li>
                <li><a href="#amenities" className="hover:text-teal-400 transition-colors">Amenities</a></li>
                <li><a href="#rates" className="hover:text-teal-400 transition-colors">Rates</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-slate-400">
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  {showPhone ? propertyData.contact.phone : 'Contact us to view phone'}
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  {showEmail ? propertyData.contact.email : 'Contact us to view email'}
                </li>
                <li className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  {propertyData.location}
                </li>
              </ul>
            </div>
          </div>
          
          <Separator className="bg-slate-700 mb-8" />
          
          <div className="text-center text-slate-400">
            <p>&copy; 2025 Casa Bahiamar. All rights reserved. | Luxury vacation rental in Key Largo, Florida</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
